<template>
  <v-app id="app" v-bind:style="{ background: appBackground }">
    <i-the-snack-bar />
    <router-view />
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import iTheSnackBar from '@/components/TheSnackBar'
import { defaultLightThemeColors } from '@/plugins/vuetify'
import { fetchExpiringAgentInvitationCount } from '@/services/invitation'

export default {
  name: 'App',

  components: {
    iTheSnackBar
  },

  data: () => ({
    expiringAgentInvitationNotificationSupplierId: null
  }),

  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('tenant', ['tenantThemeColours']),

    selectedSupplierId() {
      return this.$store.state.preferences.selectedSupplierId
    },

    emailVerified() {
      return (
        this.$route.query.success === 'true' &&
        this.$route.query.supportSignUp === 'true'
      )
    },

    appBackground() {
      return (
        this.tenantThemeColours.background || defaultLightThemeColors.background
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('tenant', ['fetchTenantSettings', 'faviconUrls']),

    async notifyAboutExpiringAgentInvitations() {
      if (!this.isAuthenticated) {
        this.expiringAgentInvitationNotificationSupplierId = null
        return
      }

      if (
        !this.selectedSupplierId ||
        this.expiringAgentInvitationNotificationSupplierId ===
          this.selectedSupplierId
      ) {
        return
      }

      try {
        const count = await fetchExpiringAgentInvitationCount(
          this.selectedSupplierId
        )
        if (count > 0) {
          this.addNotification({
            color: 'warning',
            text: `${count} agent invitation${count === 1 ? '' : 's'} will expire within 5 days.`,
            timeout: 10000
          })
        }
        this.expiringAgentInvitationNotificationSupplierId =
          this.selectedSupplierId
      } catch (error) {
        console.log(error)
      }
    },

    setVuetifyTheme(theme) {
      const themes = this.$vuetify.theme.themes
      const themeCollection = themes.value || themes
      const lightTheme = themeCollection.light || { colors: {} }

      themeCollection.light = {
        ...lightTheme,
        colors: {
          ...(lightTheme.colors || {}),
          ...theme
        }
      }
    },

    setFaviconForTenant() {
      const favicons = this.$store.getters['tenant/faviconUrls']
      if (!favicons) return

      document
        .querySelectorAll('link[rel*="icon"], link[rel="apple-touch-icon"]')
        .forEach((el) => el.remove())

      Object.entries(favicons).forEach(([size, href]) => {
        if (!href) return

        const link = document.createElement('link')
        link.href = href

        if (size === 'apple') {
          link.rel = 'apple-touch-icon'
          link.sizes = '180x180'
        } else {
          link.rel = 'icon'
          link.sizes = size
          link.type = 'image/png'
        }

        document.head.appendChild(link)
      })
    }
  },

  async mounted() {
    await this.fetchTenantSettings()
    this.setVuetifyTheme(this.tenantThemeColours)
    this.setFaviconForTenant()
    this.notifyAboutExpiringAgentInvitations()
  },

  updated() {
    if (this.emailVerified && this.isAuthenticated) {
      this.addNotification({
        color: 'secondary',
        closable: true,
        text: 'Your account is now verified and ready to use'
      })

      this.$router.replace({ query: '' })
    }
  },

  watch: {
    isAuthenticated: 'notifyAboutExpiringAgentInvitations',
    selectedSupplierId: 'notifyAboutExpiringAgentInvitations'
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro&display=swap');

#app {
  height: inherit;
  font-family: $font-family;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  .v-application--wrap,
  .v-application__wrap {
    min-height: unset !important; // 24-03-2023 Note: this addresses a bug that was very difficult to track down which
    // was resulting in the site header on mobile getting cropped and the user was unable
    // to scroll back up to view it. I'm not sure if this is the best fix but it was the
    // only fix I could find after over a day of investigation!
  }
}
</style>
